import { Link } from '@tanstack/react-router';
import type { BlogPost } from '@/types/blog';
import { categoryLabels } from '@/types/blog';

interface BlogCardFeaturedProps {
  post: BlogPost;
  readingTime: number;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

export function BlogCardFeatured({ post, readingTime }: BlogCardFeaturedProps) {
  const { title, description, publishDate, category, image, author } = post.frontmatter;

  return (
    <article className="group relative bg-white rounded-2xl border border-neutral-200 hover:border-gold-300 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden">
      <Link to="/blog/$slug" params={{ slug: post.slug }} className="block md:grid md:grid-cols-2 gap-0">
        {/* Image */}
        <div className="aspect-[16/9] md:aspect-auto md:h-full overflow-hidden">
          <img
            src={image.src}
            alt={image.alt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>

        {/* Content */}
        <div className="p-8 md:p-10 flex flex-col justify-center">
          {/* Featured Badge */}
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1.5 bg-gold-500 text-white rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Destaque
            </span>
            <span className="text-gold-600 font-semibold text-xs uppercase tracking-wider">
              {categoryLabels[category]}
            </span>
          </div>

          {/* Title */}
          <h2 className="text-2xl md:text-3xl font-bold text-navy-900 mb-4 group-hover:text-gold-600 transition-colors">
            {title}
          </h2>

          {/* Description */}
          <p className="text-neutral-600 text-lg mb-6 line-clamp-3">
            {description}
          </p>

          {/* Meta */}
          <div className="flex items-center gap-4 text-sm text-neutral-500">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gold-100 flex items-center justify-center">
                <span className="text-gold-700 font-semibold text-sm">
                  {author.name.charAt(0)}
                </span>
              </div>
              <span className="font-medium text-navy-700">{author.name}</span>
            </div>
            <span className="text-neutral-300">•</span>
            <time dateTime={publishDate}>
              {formatDate(publishDate)}
            </time>
            <span className="text-neutral-300">•</span>
            <span>{readingTime} min de leitura</span>
          </div>

          {/* CTA */}
          <div className="mt-6">
            <span className="inline-flex items-center gap-2 text-gold-600 font-semibold group-hover:gap-3 transition-all">
              Ler artigo
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}
